import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search as SearchIcon } from "lucide-react";
import {
  locationOptions,
  propertyTypeOptions,
  priceOptions,
} from "@/config/idxSearch";

/**
 * On-brand compact property search bar (not an IDX widget).
 * Submits to the internal /search page so results render inline on our domain.
 */
const PropertySearchBar = ({ className = "" }: { className?: string }) => {
  const [locationId, setLocationId] = useState("");
  const [type, setType] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const navigate = useNavigate();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (locationId) params.append("loc", locationId);
    if (type) params.append("type", type);
    if (maxPrice) params.append("max", maxPrice);
    const query = params.toString();
    navigate(query ? `/search?${query}` : "/search");
  };

  const fieldClass =
    "w-full h-12 px-4 bg-background border border-border rounded-sm text-foreground font-medium focus:outline-none focus:border-primary/60 focus:ring-1 focus:ring-primary/30 transition-colors";

  return (
    <form
      onSubmit={handleSubmit}
      className={`bg-background border border-border rounded-sm shadow-[0_14px_40px_-24px_hsl(0_0%_0%/0.35)] p-5 md:p-6 ${className}`}
    >
      <div className="flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[160px]">
          <label htmlFor="psb-loc" className="block text-xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap mb-2 text-left">
            Location
          </label>
          <select id="psb-loc" value={locationId} onChange={(e) => setLocationId(e.target.value)} className={fieldClass}>
            <option value="">All New Jersey</option>
            {locationOptions.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-[150px]">
          <label htmlFor="psb-type" className="block text-xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap mb-2 text-left">
            Property Type
          </label>
          <select id="psb-type" value={type} onChange={(e) => setType(e.target.value)} className={fieldClass}>
            {propertyTypeOptions.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-[150px]">
          <label htmlFor="psb-price" className="block text-xs font-medium uppercase tracking-wide text-muted-foreground whitespace-nowrap mb-2 text-left">
            Max Price
          </label>
          <select id="psb-price" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className={fieldClass}>
            <option value="">No Max</option>
            {priceOptions.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          className="h-12 flex-1 min-w-[180px] md:flex-none inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 rounded-sm uppercase tracking-[0.2em] text-xs font-bold hover:bg-primary/90 transition-colors whitespace-nowrap"
        >
          <SearchIcon className="h-4 w-4" strokeWidth={2} />
          Search Homes
        </button>
      </div>
    </form>
  );
};

export default PropertySearchBar;
